import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Check, Loader } from 'lucide-react';

function PaymentPage() {
  const [selectedPackage, setSelectedPackage] = useState('100');
  const [tipAmount, setTipAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [polling, setPolling] = useState(false);

  const packages = [
    { id: '25', amount: 25, label: '$25' },
    { id: '50', amount: 50, label: '$50' },
    { id: '100', amount: 100, label: '$100' },
    { id: '150', amount: 150, label: '$150' },
    { id: '200', amount: 200, label: '$200' }
  ];

  const selectedAmount = packages.find(p => p.id === selectedPackage)?.amount || 0;
  const tip = parseFloat(tipAmount) || 0;
  const total = selectedAmount + tip;

  // Check for return from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    setPolling(true);
    const maxAttempts = 5;

    if (attempts >= maxAttempts) {
      alert('Payment verification timed out. Please check your email.');
      setPolling(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === 'paid') {
        alert('Payment successful! Welcome to The Belonging Sessions!');
        window.location.href = '/dashboard';
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      setPolling(false);
      alert('Error verifying payment. Please contact support.');
    }
  };

  const handleCheckout = async () => {
    setProcessing(true);

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package: selectedPackage,
          tip_amount: tip,
          origin_url: originUrl
        })
      });

      if (!response.ok) throw new Error('Failed to create checkout');

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      alert('Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  if (polling) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p className="text-2xl font-bold">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(138 25% 96%)' }}>
      <div className="max-w-4xl mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4">Unlock The Full Course</h1>
          <p className="text-2xl text-gray-700">Choose your investment level</p>
        </div>

        <Card style={{ borderRadius: 0, border: '3px solid black' }}>
          <CardHeader style={{ backgroundColor: 'white' }}>
            <CardTitle className="text-3xl">Sliding Scale Pricing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8 mt-6">
            <RadioGroup value={selectedPackage} onValueChange={setSelectedPackage}>
              <div className="grid md:grid-cols-5 gap-4">
                {packages.map(pkg => (
                  <div key={pkg.id} className="relative">
                    <RadioGroupItem value={pkg.id} id={pkg.id} className="peer sr-only" />
                    <label
                      htmlFor={pkg.id}
                      className={`block p-6 border-4 cursor-pointer transition-all text-center ${
                        selectedPackage === pkg.id
                          ? 'bg-black text-white border-black'
                          : 'bg-white border-gray-300 hover:border-black'
                      }`}
                    >
                      <p className="text-3xl font-bold mb-2">{pkg.label}</p>
                      {selectedPackage === pkg.id && <Check className="w-6 h-6 mx-auto" />}
                    </label>
                  </div>
                ))}
              </div>
            </RadioGroup>

            <div className="p-6 rounded-sm" style={{ backgroundColor: 'hsl(138 20% 90%)' }}>
              <Label className="text-lg font-semibold mb-3 block">Add a Tip (Optional)</Label>
              <p className="text-sm text-gray-700 mb-4">
                Support our work to make NVC accessible to all. Any amount appreciated!
              </p>
              <div className="flex items-center gap-4">
                <span className="text-2xl font-bold">$</span>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  className="text-2xl py-6 border-black"
                  style={{ borderRadius: 0 }}
                />
              </div>
            </div>

            <div className="p-8 bg-black text-white">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <p className="text-lg">Course Investment:</p>
                  <p className="text-lg">Tip:</p>
                  <p className="text-2xl font-bold mt-2">Total:</p>
                </div>
                <div className="text-right">
                  <p className="text-lg">${selectedAmount.toFixed(2)}</p>
                  <p className="text-lg">${tip.toFixed(2)}</p>
                  <p className="text-4xl font-bold mt-2">${total.toFixed(2)}</p>
                </div>
              </div>
              <Button
                onClick={handleCheckout}
                disabled={processing}
                className="w-full bg-white text-black hover:bg-white/90 py-8 text-2xl font-bold"
                style={{ borderRadius: 0 }}
              >
                {processing ? 'Redirecting to checkout...' : 'Proceed to Secure Checkout'}
              </Button>
            </div>

            <p className="text-sm text-gray-600 text-center">
              Partial scholarships available through Defiance Springs family fund. Contact us to inquire.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default PaymentPage;
